<script lang="ts" setup>
import { QAvatar } from 'quasar'
import { computed } from 'vue'
import { Color } from '@previo/codebooks'
import PIcon from '../icon/PIcon.vue'
import PTextHighlighted from '../typography/body/PTextHighlighted.vue'
import PTextTitle2 from '../typography/title/PTextTitle2.vue'
import { PAvatarSize, PAvatarType } from './types'

interface Props {
  type: PAvatarType
  size?: PAvatarSize
  icon?: null | string
  text?: null | string
  rawText?: null | string
  photo?: string
  color?: Color.GREY_500 | Color.NEGATIVE | Color.POSITIVE | Color.PRIMARY
  enableHover?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  size: PAvatarSize.MEDIUM,
  text: null,
  rawText: null,
  icon: null,
  color: Color.GREY_500,
  enableHover: false,
})

const initials = computed<string>(() => {
  const maxInitials = 3

  if (!props.text) return ''

  const words = props.text.trim().split(' ')
  const initials = words
    .map((word) => word[0])
    .join('')
    .toUpperCase()

  return initials.slice(0, maxInitials)
})

const displayText = computed<null | string>(() => {
  return props.rawText || initials.value
})

const backgroundColor = computed<
  | Color.NEGATIVE_TRANSPARENT
  | Color.POSITIVE_TRANSPARENT
  | Color.PRIMARY_TRANSPARENT
  | Color.GREY_200
>(() => {
  if (props.color === Color.NEGATIVE) {
    return Color.NEGATIVE_TRANSPARENT
  } else if (props.color === Color.POSITIVE) {
    return Color.POSITIVE_TRANSPARENT
  } else if (props.color === Color.PRIMARY) {
    return Color.PRIMARY_TRANSPARENT
  }

  return Color.GREY_200
})

const textColor = computed<
  Color.NEGATIVE_DARK | Color.POSITIVE_DARK | Color.PRIMARY_DARK | Color.GREY_600
>(() => {
  if (props.color === Color.NEGATIVE) {
    return Color.NEGATIVE_DARK
  } else if (props.color === Color.POSITIVE) {
    return Color.POSITIVE_DARK
  } else if (props.color === Color.PRIMARY) {
    return Color.PRIMARY_DARK
  }

  return Color.GREY_600
})
</script>

<template>
  <q-avatar
    :class="[{ 'ds-avatar-hover': enableHover }, `ds-avatar-${size}`]"
    :color="backgroundColor"
  >
    <p-icon v-if="type === PAvatarType.ICON && icon !== null" :icon="icon" :color="color" />
    <template v-if="type === PAvatarType.MONOGRAM">
      <p v-if="size === PAvatarSize.EXTRA_SMALL" :class="`text-${textColor}`">
        {{ displayText }}
      </p>
      <p-text-highlighted
        v-else-if="size === PAvatarSize.SMALL"
        :text="displayText"
        :color="textColor"
      />
      <p-text-title2 v-else :text="displayText" :color="textColor" />
    </template>
    <img v-else-if="type === PAvatarType.AVATAR" :src="photo" alt="Photo of avatar" />
    <slot />
  </q-avatar>
</template>

<style lang="scss" scoped>
.ds-avatar-hover {
  cursor: pointer;
  transition: box-shadow 300ms linear;

  &:hover {
    box-shadow: rgba(10, 10, 10, 0.12) 0 4px 8px 0;
  }
}

.ds-avatar-extra-small {
  font-size: 14px;
  width: 16px;
  height: 16px;
}

.ds-avatar-small {
  width: 36px;
  height: 36px;
}

.ds-avatar-medium {
  width: 40px;
  height: 40px;
}

.ds-avatar-large {
  width: 44px;
  height: 44px;
}
</style>
